/**
 * CohortComparator — tableau côte-à-côte des KPI des apprentis d'une cohorte.
 *
 * 11/02/2026 (Phase 3 v2 — Superpouvoirs formateur).
 *
 * Rendu :
 *   - Sélecteur de cohorte
 *   - Tableau : Apprenti | Jour | Phase | Trésorerie | Notoriété | Contrats | Résultat
 *   - Tri par trésorerie décroissante (par défaut)
 *   - Highlight du meilleur / pire pour chaque colonne
 *
 * L'endpoint /api/master/cohorts/{id}/comparator retourne les snapshots
 * enrichis (via `enrich_snapshot`). Certains apprentis peuvent ne pas
 * avoir de snapshot (jamais joué) → affiche "—".
 */
import React, { useState, useEffect, useMemo } from 'react';
import { BarChart3, TrendingUp, TrendingDown, Loader2 } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function fmtMoney(v) {
  if (v == null || !Number.isFinite(v)) return '—';
  const rounded = Math.round(v);
  return rounded.toLocaleString('fr-FR') + ' €';
}

function fmtNumber(v, unit = '') {
  if (v == null) return '—';
  return `${v}${unit}`;
}

export default function CohortComparator({ cohorts }) {
  const [selectedCohortId, setSelectedCohortId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!selectedCohortId) { setData(null); return undefined; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`${BACKEND}/api/master/cohorts/${selectedCohortId}/comparator`, {
      credentials: 'include',
    })
      .then(async (r) => {
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err.detail || `HTTP ${r.status}`);
        }
        return r.json();
      })
      .then((d) => { if (!cancelled) setData(d); })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedCohortId]);

  // Calcule les min/max par métrique pour highlight.
  const stats = useMemo(() => {
    if (!data?.items) return {};
    const items = data.items.filter((it) => it.snapshot);
    if (items.length === 0) return {};
    const values = {
      treasury: items.map((it) => it.snapshot.treasury || 0),
      reputation: items.map((it) => it.snapshot.reputation || 0),
      days: items.map((it) => it.snapshot.total_days || 0),
    };
    const s = {};
    for (const [k, arr] of Object.entries(values)) {
      s[k] = { min: Math.min(...arr), max: Math.max(...arr) };
    }
    return s;
  }, [data]);

  const cellClass = (metric, value, higherIsBetter = true) => {
    const stat = stats[metric];
    if (!stat || value == null) return '';
    if (stat.min === stat.max) return '';
    const isTop = higherIsBetter ? value === stat.max : value === stat.min;
    const isBottom = higherIsBetter ? value === stat.min : value === stat.max;
    if (isTop) return 'text-emerald-300 font-bold';
    if (isBottom) return 'text-red-300';
    return 'text-slate-300';
  };

  return (
    <section className="bg-ink-800/60 border border-ink-600 rounded-lg p-4" data-testid="cohort-comparator-section">
      <header className="flex items-center gap-2 mb-3">
        <BarChart3 className="w-4 h-4 text-emerald-400" />
        <h3 className="font-display text-sm font-bold tracking-tight uppercase text-foreground">
          Comparateur pairs
        </h3>
      </header>
      <p className="text-xs text-slate-400 mb-3">
        Comparez les KPI clés de vos apprentis d&apos;une cohorte, en temps réel (dernier snapshot reçu).
      </p>

      <select
        value={selectedCohortId}
        onChange={(e) => setSelectedCohortId(e.target.value)}
        className="w-full bg-ink-950 border border-ink-600 rounded px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-accent mb-3"
        data-testid="comparator-cohort-select"
      >
        <option value="">— Choisir une cohorte —</option>
        {cohorts.map((c) => (
          <option key={c.id} value={c.id}>{c.name} ({c.apprentice_keys.length})</option>
        ))}
      </select>

      {loading && (
        <div className="text-xs text-slate-400 flex items-center gap-2" data-testid="comparator-loading">
          <Loader2 className="w-3 h-3 animate-spin" /> Chargement…
        </div>
      )}
      {error && (
        <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/40 rounded p-2" data-testid="comparator-error">
          {error}
        </div>
      )}

      {data && !loading && (
        data.items.length === 0 ? (
          <div className="text-xs text-slate-500 italic" data-testid="comparator-empty">
            Aucun apprenti dans cette cohorte.
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="comparator-table-wrapper">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-ink-600 text-slate-400 text-left uppercase text-[10px] tracking-widest">
                  <th className="py-1.5 pr-3">Apprenti</th>
                  <th className="py-1.5 pr-3">Jour</th>
                  <th className="py-1.5 pr-3">Phase</th>
                  <th className="py-1.5 pr-3 text-right">Trésorerie</th>
                  <th className="py-1.5 pr-3">Notoriété</th>
                  <th className="py-1.5 pr-3">Flotte</th>
                  <th className="py-1.5">Contrats</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((it) => {
                  const snap = it.snapshot;
                  const noSnap = !snap;
                  return (
                    <tr key={it.license_key} className="border-b border-ink-700/50 hover:bg-ink-700/30" data-testid={`comparator-row-${it.license_key}`}>
                      <td className="py-2 pr-3">
                        <div className="text-slate-200 font-medium">{it.holder_name || '—'}</div>
                        <div className="ph-no-capture text-[10px] text-slate-500 font-mono">{it.license_key.slice(0, 8)}</div>
                      </td>
                      {noSnap ? (
                        <td colSpan={6} className="py-2 text-[11px] text-slate-500 italic">Aucun snapshot reçu</td>
                      ) : (
                        <>
                          <td className={`py-2 pr-3 ${cellClass('days', snap.total_days)}`}>
                            {fmtNumber(snap.total_days, ' j')}
                          </td>
                          <td className="py-2 pr-3 text-slate-300">{fmtNumber(snap.phase)}</td>
                          <td className={`py-2 pr-3 text-right font-mono ${cellClass('treasury', snap.treasury)}`}>
                            {fmtMoney(snap.treasury)}
                          </td>
                          <td className={`py-2 pr-3 ${cellClass('reputation', snap.reputation)}`}>
                            {fmtNumber(snap.reputation, '/100')}
                          </td>
                          <td className="py-2 pr-3 text-slate-300">
                            {fmtNumber(snap.fleet_active)}/{fmtNumber(snap.fleet_total)}
                          </td>
                          <td className="py-2 text-slate-300">{fmtNumber(snap.contracts_active)}</td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="mt-2 flex items-center gap-4 text-[10px] text-slate-500">
              <span className="flex items-center gap-1"><TrendingUp className="w-3 h-3 text-emerald-300" /> Meilleur</span>
              <span className="flex items-center gap-1"><TrendingDown className="w-3 h-3 text-red-300" /> Plus bas</span>
            </div>
          </div>
        )
      )}
    </section>
  );
}
